import { useEffect, useState } from 'react';
import { ListSubheader, alpha, Box, List, styled } from '@mui/material';
import { useLocation, matchPath } from 'react-router-dom';
import SidebarMenuItem from './item';
import menuItems, { MenuItem /*, MenuItems */ } from './items';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'react-admin';
import { canAccess } from '@react-admin/ra-rbac';
import { brandGradients } from '../../../themes/brandTokens';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(0.5, 1.5)};

    & > .MuiList-root {
      padding: 0;
    }
  }

  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: 600;
    font-size: ${theme.typography.pxToRem(11)};
    letter-spacing: 0.08em;
    color: ${theme.colors.alpha.trueWhite[50]};
    background: transparent;
    padding: ${theme.spacing(2, 1.5, 1)};
    line-height: 1.4;
  }
`
);

// Top-level items carry an icon; sub-items hang off a thin guide line. The gradient bar
// marks the section of the current page, not merely an expanded group.
const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }

        .MuiButton-root {
          position: relative;
          display: flex;
          width: 100%;
          min-height: 40px;
          justify-content: flex-start;
          padding: ${theme.spacing(0.9, 1.5)};
          border-radius: 10px;
          font-size: ${theme.typography.pxToRem(14)};
          font-weight: 500;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          transition: ${theme.transitions.create(['background-color', 'color'], { duration: 150 })};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color', 'transform'], { duration: 150 })};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-left: 0;
            margin-right: ${theme.spacing(1.4)};
          }

          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            font-size: ${theme.typography.pxToRem(18)};
          }

          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.05)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }

          &.Mui-focusVisible {
            outline-color: ${theme.colors.alpha.trueWhite[100]};
          }

          &.open .MuiButton-endIcon {
            transform: rotate(180deg);
          }

          &.active {
            background-color: ${theme.sidebar.menuItemBgActive};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }

            &::after {
              content: '';
              position: absolute;
              left: -12px;
              top: 9px;
              bottom: 9px;
              width: 3px;
              border-radius: 0 3px 3px 0;
              background: ${brandGradients.accentBar};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            position: relative;
            padding: ${theme.spacing(0.5, 0, 0.75, 4.25)};

            &::before {
              content: '';
              position: absolute;
              left: 21px;
              top: 6px;
              bottom: 10px;
              width: 1px;
              background: ${theme.colors.alpha.trueWhite[10]};
            }
          }

          .MuiListItem-root {
            padding: 0;

            .MuiButton-root {
              min-height: 34px;
              padding: ${theme.spacing(0.6, 1.5)};
              font-size: ${theme.typography.pxToRem(13.5)};
              font-weight: 400;
              color: ${theme.colors.alpha.trueWhite[50]};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:hover {
                background-color: transparent;
                color: ${theme.colors.alpha.trueWhite[100]};
              }

              &.active {
                background-color: transparent;
                color: ${theme.colors.alpha.trueWhite[100]};
                font-weight: 600;

                /* The current page is marked on the guide line itself. */
                &::after {
                  left: -14px;
                  top: 9px;
                  bottom: 9px;
                  width: 3px;
                  border-radius: 3px;
                }
              }
            }
          }
        }
      }
    }
`
);

const renderSidebarMenuItems = ({
  items,
  path
}: {
  items: MenuItem[];
  path: string;
}): JSX.Element => (
  <SubMenuWrapper>
    <List component="div">
      {items.reduce((ev, item) => reduceChildRoutes({ ev, item, path }), [])}
    </List>
  </SubMenuWrapper>
);

const reduceChildRoutes = ({
  ev,
  path,
  item
}: {
  ev: JSX.Element[];
  path: string;
  item: MenuItem;
}): Array<JSX.Element> => {
  const key = item.name;
  const exactMatch = item.link
    ? !!matchPath(
        {
          path: item.link,
          end: true
        },
        path
      )
    : false;

  if (item.items) {
    // Groups have no link of their own: a group is current when one of its pages is.
    const partialMatch = item.items.some(
      (child) => !!child.link && !!matchPath({ path: child.link, end: false }, path)
    );

    ev.push(
      <SidebarMenuItem
        key={key}
        active={partialMatch}
        open={partialMatch}
        name={item.name}
        icon={item.icon}
        link={item.link}
        badge={item.badge}
        badgeTooltip={item.badgeTooltip}
      >
        {renderSidebarMenuItems({
          path,
          items: item.items
        })}
      </SidebarMenuItem>
    );
  } else {
    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        name={item.name}
        link={item.link}
        badge={item.badge}
        badgeTooltip={item.badgeTooltip}
        icon={item.icon}
      />
    );
  }

  return ev;
};

export interface MenuItems {
  items: MenuItem[];
  heading: string;
  permission?: string[];
}


function SidebarMenu() {
  const location = useLocation();
  const { t }: { t: any } = useTranslation();
  const [filteredMenu, setFilteredMenu] = useState<MenuItems[]>([]);
  const { permissions, isLoading } = usePermissions();

  useEffect(() => {
    const filterMenuItemRecursively = (item: MenuItem): MenuItem | null => {
      const filteredSubItems = item.items
        ? item.items
            .map(filterMenuItemRecursively)
            .filter((subItem): subItem is MenuItem => subItem !== null)
        : [];
    
      const hasAccess = item.ressource
        ? item.ressource.some(resource =>
            canAccess({ permissions, resource, action: item.action }),
          )
        : true;
    
      if (!hasAccess && filteredSubItems.length === 0) {
        return null;
      }
    
      return {
        ...item,
        items: filteredSubItems.length > 0 ? filteredSubItems : undefined,
      };
    };
    
  
    setFilteredMenu(
      menuItems
        .map(section => {
          const filteredItems = section.items
            .map(filterMenuItemRecursively)
            .filter((item): item is MenuItem => item !== null);
    
          return {
            ...section,
            items: filteredItems,
          };
        })
        .filter(section => section.items.length > 0)
    );
  }, [permissions, isLoading]);
  

  return (
    <>
      {filteredMenu.map(
        section =>
          section.items.length > 0 && (
            <MenuWrapper key={section.heading}>
              <List
                component="div"
                subheader={
                  <ListSubheader component="div" disableSticky>
                    {t(section.heading)}
                  </ListSubheader>
                }
              >
                {renderSidebarMenuItems({
                  items: section.items,
                  path: location.pathname,
                })}
              </List>
            </MenuWrapper>
          ),
      )}
    </>
  );
}

export default SidebarMenu;
